import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import Link from 'flarum/common/components/Link';
import classList from 'flarum/common/utils/classList';
import { activeSection, sectionNumber, sections, subsections } from '../utils/sections';

/**
 * Sections shown in the desktop row (and its "Ещё"): all of them except those listed in the
 * tarotportal-theme.nav_hidden_tags setting (slugs, comma-separated; default "magi-sharlatany").
 * Hidden sections stay everywhere else — the mobile menu, the home grid, their own pages.
 */
function rowSections() {
  const hidden = String(app.forum.attribute('tarotportalNavHiddenTags') || '').split(',').map(slug => slug.trim()).filter(Boolean);
  return sections().filter(tag => !hidden.includes(tag.slug()));
}

/**
 * Second header row with the forum sections. On desktop: every section that fits, in order;
 * the trailing ones that do not fit go under "Ещё" (measured, like core's OverflowingList).
 * In the mobile drawer: an accordion of all sections with their subsections.
 */
export default class SectionsNav extends Component {
  moreOpen = false;
  expanded = null;

  /** How many sections are shown in the row; null while measuring (all rendered). */
  visibleCount = null;
  widths = [];
  toggleWidth = 0;
  closeOnEscape = e => {
    if (e.key !== 'Escape' || !this.moreOpen) return;
    this.moreOpen = false;
    this.element?.querySelector('.TpSections-moreToggle')?.focus();
    m.redraw();
  };
  closeOnOutside = e => {
    if (this.moreOpen && !e.target.closest('.TpSections-moreToggle, .TpSections-more')) {
      this.moreOpen = false;
      m.redraw();
    }
  };
  oncreate(vnode) {
    super.oncreate(vnode);
    document.addEventListener('click', this.closeOnOutside);
    document.addEventListener('keydown', this.closeOnEscape);
    const row = this.element.querySelector('.TpSections-row');
    if (typeof ResizeObserver !== 'undefined' && row) {
      this.observer = new ResizeObserver(() => this.fit());
      this.observer.observe(row);
    }

    // Widths change once the web fonts arrive: measure the full row again.
    document.fonts?.ready.then(() => this.remeasure());
    this.fit();
  }
  onupdate(vnode) {
    super.onupdate(vnode);
    this.fit();
  }
  onremove(vnode) {
    super.onremove(vnode);
    document.removeEventListener('click', this.closeOnOutside);
    document.removeEventListener('keydown', this.closeOnEscape);
    clearTimeout(this.closeTimer);
    this.observer?.disconnect();
  }
  remeasure() {
    this.widths = [];
    this.visibleCount = null;
    m.redraw();
  }

  /** Show as many sections as fit; the rest go to "Ещё". Never wraps, never shrinks the text. */
  fit() {
    const row = this.element?.querySelector('.TpSections-row');
    if (!row || getComputedStyle(row).display === 'none') return;
    const links = Array.from(row.querySelectorAll('.TpSections-item-link'));
    const total = rowSections().length;
    if (this.visibleCount === null || this.widths.length !== total) {
      if (links.length !== total) return;
      this.widths = links.map(link => link.getBoundingClientRect().width);
    }
    const toggle = row.querySelector('.TpSections-moreToggle');
    if (toggle) this.toggleWidth = toggle.getBoundingClientRect().width;
    const style = getComputedStyle(row);
    const gap = parseFloat(style.columnGap) || 0;
    const available = row.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
    const used = count => this.widths.slice(0, count).reduce((sum, w) => sum + w, 0) + gap * Math.max(0, count - 1);
    let count = total;
    if (used(total) > available) {
      const toggleSpace = gap + (this.toggleWidth || 48);
      while (count > 0 && used(count) + toggleSpace > available) count--;
    }
    if (count !== this.visibleCount) {
      this.visibleCount = count;
      if (count === total) this.moreOpen = false;
      m.redraw();
    }
  }

  /**
   * "Ещё" opens on hover as on the portal (mouse only; a short delay lets the pointer travel from
   * the toggle to the panel). Click / Enter toggle it as before; Escape closes it.
   */
  hoverOpen(open) {
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    clearTimeout(this.closeTimer);
    if (open) {
      if (!this.moreOpen) {
        this.moreOpen = true;
        m.redraw();
      }
      return;
    }
    this.closeTimer = window.setTimeout(() => {
      this.moreOpen = false;
      m.redraw();
    }, 180);
  }
  view() {
    const all = sections();
    const inRow = rowSections();
    const active = activeSection();
    if (!all.length) return null;
    const count = this.visibleCount ?? inRow.length;
    const rest = inRow.slice(count);
    return m("nav", {
      className: "TpSections",
      "aria-label": app.translator.trans('tarotportal-theme.forum.nav.sections_heading', {}, true)
    }, m("div", {
      className: "TpSections-row"
    }, inRow.slice(0, count).map(tag => m(Link, {
      className: classList('TpSections-link TpSections-item-link', {
        active: tag === active
      }),
      href: app.route.tag(tag)
    }, tag.name())), rest.length > 0 && m("button", {
      type: "button",
      className: classList('TpSections-link TpSections-moreToggle', {
        active: this.moreOpen || rest.includes(active),
        current: rest.includes(active)
      }),
      "aria-expanded": this.moreOpen ? 'true' : 'false',
      onclick: e => {
        // a mouse click right after hovering keeps it open; keyboard (detail 0) and touch toggle
        const hovered = e.detail > 0 && window.matchMedia('(hover: hover) and (pointer: fine)').matches;
        this.moreOpen = hovered ? true : !this.moreOpen;
      },
      onmouseenter: () => this.hoverOpen(true),
      onmouseleave: () => this.hoverOpen(false)
    }, app.translator.trans('tarotportal-theme.forum.nav.more'))), this.moreOpen && rest.length > 0 && this.morePanel(rest, active), this.drawerList(all, active));
  }
  morePanel(rest, active) {
    return m("div", {
      className: "TpSections-more",
      onclick: e => e.target.closest('a') && (this.moreOpen = false),
      onmouseenter: () => this.hoverOpen(true),
      onmouseleave: () => this.hoverOpen(false)
    }, m("div", {
      className: "TpSections-moreGrid"
    }, rest.map(tag => m(Link, {
      className: classList('TpSections-moreItem', {
        active: tag === active
      }),
      href: app.route.tag(tag)
    }, m("span", {
      className: "TpSections-moreName"
    }, tag.name()), m("span", {
      className: "TpSections-moreText"
    }, tag.description())))));
  }
  drawerList(all, active) {
    return m("div", {
      className: "TpSections-drawer"
    }, m("button", {
      type: "button",
      className: "TpSections-close",
      "aria-label": app.translator.trans('core.lib.modal.close', {}, true),
      onclick: () => app.drawer.hide()
    }, m("span", {
      "aria-hidden": "true"
    }, "\u2715")), m("p", {
      className: "TpEyebrow TpEyebrow--muted"
    }, app.translator.trans('tarotportal-theme.forum.nav.sections_heading')), m("ul", {
      className: "TpSections-list"
    }, all.map(tag => {
      const children = subsections(tag);
      const open = this.expanded === tag.id() || this.expanded === null && tag === active;
      return m("li", {
        className: classList('TpSections-item', {
          open
        })
      }, m("div", {
        className: "TpSections-itemRow"
      }, m(Link, {
        className: "TpSections-itemLink",
        href: app.route.tag(tag)
      }, m("span", {
        className: "TpNum"
      }, sectionNumber(tag)), tag.name()), children.length > 0 && m("button", {
        type: "button",
        className: "TpSections-expand",
        "aria-expanded": open ? 'true' : 'false',
        "aria-label": tag.name(),
        onclick: () => this.expanded = open ? '' : tag.id()
      }, m("span", {
        "aria-hidden": "true"
      }, open ? '−' : '+'))), open && children.length > 0 && m("ul", {
        className: "TpSections-children"
      }, children.map(child => m("li", null, m(Link, {
        href: app.route.tag(child)
      }, child.name())))));
    })));
  }
}
flarum.reg.add('tarotportal-theme', 'forum/components/SectionsNav', SectionsNav);